import { forwardRef, type HTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/cn';

/**
 * A larger, bolder sibling to Badge — for a severity/risk verdict meant to be
 * read at a glance (Pre-Consult's Risk level selector, an Assessment
 * Module's overall Green/Yellow/Red), not an inline table-row status.
 *
 * Same five semantic tones as Badge, same theme.css tokens — a RiskBadge and
 * a Badge on the same screen must agree on what "danger" looks like. This
 * replaces the hardcoded hex pairs that PreConsultStage.jsx's RISK_COLORS
 * and AssessmentSummaryCard.jsx's COLOR_META each carried independently
 * before this existed.
 */
const riskBadgeVariants = cva(
  'inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[13px] font-bold leading-none',
  {
    variants: {
      tone: {
        neutral: 'bg-muted text-muted-foreground',
        success: 'bg-success-soft text-success-ink',
        warn: 'bg-warn-soft text-warn-ink',
        caution: 'bg-warn-soft text-caution-ink',
        danger: 'bg-danger-soft text-danger-ink',
        info: 'bg-info-soft text-info-ink',
      },
    },
    defaultVariants: { tone: 'neutral' },
  }
);

export interface RiskBadgeProps extends HTMLAttributes<HTMLSpanElement>, VariantProps<typeof riskBadgeVariants> {}

export const RiskBadge = forwardRef<HTMLSpanElement, RiskBadgeProps>(({ className, tone, ...props }, ref) => (
  <span ref={ref} className={cn(riskBadgeVariants({ tone }), className)} {...props} />
));
RiskBadge.displayName = 'RiskBadge';

export type RiskTone = NonNullable<VariantProps<typeof riskBadgeVariants>['tone']>;

/** Pre-Consult's Risk level scale (Low/Medium/High/Critical), mapped onto the shared tone tokens. */
export const RISK_LEVEL_TONE: Record<'Low' | 'Medium' | 'High' | 'Critical', RiskTone> = {
  Low: 'success',
  Medium: 'warn',
  High: 'caution',
  Critical: 'danger',
};

/** The Assessment Module scoring engine's Green/Yellow/Red verdict (backend/utils/assessmentScoring.js), same tone tokens. */
export const TRIAGE_TONE: Record<'green' | 'yellow' | 'red', RiskTone> = {
  green: 'success',
  yellow: 'warn',
  red: 'danger',
};
